import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { userPositions } from 'features/ManageUserDialog/userTitleTypes'
import dashboardApi from 'services/api/dashboard'
import userApi from 'services/api/user'

import { emailRegex } from '../../utils/helpers'

export const phoneNumberChange = createAsyncThunk(
  'manageUser/phoneNumberChange',
  async (phoneNumber, thunkApi) => {
    if (phoneNumber.length > 5) {
      thunkApi.dispatch(validatePhoneNumber(phoneNumber))
    }

    return phoneNumber
  }
)

export const validatePhoneNumber = createAsyncThunk(
  'manageUsers/validatePhoneNumber',
  async (phoneNumber, { rejectWithValue }) => {
    if (phoneNumber.length > 5) {
      return await dashboardApi.checkPhoneNumber(phoneNumber)
    } else {
      rejectWithValue('number too short')
    }
  }
)

export const addNewUser = createAsyncThunk(
  'manageUsers/addNewUser',
  async (
    {
      phone,
      email,
      fullName,
      ExistingUserAccept = false,
      position,
      timeZoneName,
      subsidiaryId,
      isAddingDuringSetup,
      isInvoiceRecipient
    },
    { rejectWithValue, dispatch }
  ) => {
    const data = await userApi.add({
      phone: phone ? phone : null,
      email,
      fullName,
      position,
      timeZoneName,
      existingUserAccept: ExistingUserAccept,
      subsidiaryId,
      isAddingDuringSetup,
      ExistingUserAccept,
      isInvoiceRecipient
    })

    if (data.requiresAdminAcceptanceOfExistingAccount) {
      dispatch(manageUserDialogSlice.actions.existingAccountFound(data.user))
      return rejectWithValue('foundExistingAccount')
    }

    return data
  }
)

export const editUser = createAsyncThunk(
  'manageUsers/editUser',
  async ({
    userId,
    position,
    fullName,
    phone,
    email,
    timeZoneName,
    isInvoiceRecipient
  }) => {
    return await userApi.edit({
      userId,
      position: position,
      fullName,
      phone,
      email,
      timeZoneName,
      isInvoiceRecipient
    })
  }
)

export const deleteUser = createAsyncThunk('manageUsers/deleteUser', async userId => {
  return await userApi.remove(userId)
})

const initialState = {
  userId: null,
  phone: '',
  email: '',
  fullName: '',
  timeZoneName: '',
  position: userPositions.Default,
  isPhoneValidated: null,
  isEmailValidated: null,
  isFullNameValidated: null,
  userAdded: false,
  userEdited: false,
  userAlreadyAdded: null,
  existingAccountFound: null,
  existingUserInfo: null,
  isInvoiceRecipient: false
}

const manageUserDialogSlice = createSlice({
  name: 'manageUsers',
  initialState,
  reducers: {
    dialogOpen: () => {
      return { ...initialState }
    },
    existingAccountFound: (state, action) => {
      state.existingAccountFound = true
      state.existingUserInfo = action.payload
    },
    userAlreadyAdded: state => {
      state.userAlreadyAdded = true
    },
    gotUserToEdit: (state, action) => {
      state.phone = action.payload.phone
      state.email = action.payload.email
      state.fullName = action.payload.name
      state.isInvoiceRecipient = action.payload.isInvoiceRecipient
      state.userId = action.payload.userId
      state.position = action.payload.position
      state.timeZoneName = action.payload.tzTimeZoneName
      state.isPhoneValidated = true
      state.isEmailValidated = true
      state.isFullNameValidated = true
    },
    fullNameChange: (state, action) => {
      state.fullName = action.payload

      if (action.payload && action.payload.match(/^\w+\s\w+$/)) {
        state.isFullNameValidated = true
      } else {
        state.isFullNameValidated = false
      }
    },
    positionChange: (state, action) => {
      state.position = action.payload
    },
    timezoneChange: (state, action) => {
      state.timeZoneName = action.payload
    },
    invoiceRecipientChange: (state, action) => {
      state.isInvoiceRecipient = action.payload
    },
    emailChange: (state, action) => {
      state.email = action.payload

      if (action.payload && action.payload.match(emailRegex)) {
        state.isEmailValidated = true
      } else {
        state.isEmailValidated = false
      }
    }
  },
  extraReducers: {
    [phoneNumberChange.fulfilled]: (state, action) => {
      state.phone = action.payload
    },
    [validatePhoneNumber.pending]: state => {
      state.isPhoneValidated = false
    },
    [validatePhoneNumber.fulfilled]: (state, action) => {
      if (action.payload) {
        state.phone = action.payload
        state.isPhoneValidated = true
      }
    },
    [editUser.rejected]: (state, action) => {
      state.error = action.message || 'Something went wrong'
    },
    [editUser.fulfilled]: state => {
      state.error = null
      state.userEdited = true
    },
    [addNewUser.fulfilled]: state => {
      state.error = null
      state.userAdded = true
    },
    [addNewUser.rejected]: (state, action) => {
      state.error = action.error.message
    }
  }
})

export default manageUserDialogSlice
